/**
 * mcpSeoData.ts
 *
 * Central SEO content for every MCP landing page at /mcp/:slug.
 * Separate from mcpRegistry.ts (which drives the registry UI) so we can
 * have richer editorial copy here without polluting the registry data model.
 *
 * Each entry maps 1-to-1 with an MCP_REGISTRY slug.
 */

export type McpFaq = { q: string; a: string };

export type McpUseCase = {
  title: string;
  description: string;
  icon: string;
};

export type McpSeoEntry = {
  slug: string;
  /** <title> tag — keep under 60 chars */
  pageTitle: string;
  /** <meta name="description"> — keep under 155 chars */
  metaDescription: string;
  /** H1 on the landing page */
  headline: string;
  /** Subheading / tagline */
  subheadline: string;
  /** Canonical category label shown in breadcrumb */
  category: string;
  /** What this API does — 2-3 sentences, plain prose */
  about: string;
  /** Why use it as an MCP — 2-3 sentences */
  whyMcp: string;
  /** Auth type label shown in setup section */
  authTypeName: "API Key" | "OAuth 2.0" | "None (public)";
  /** Short auth setup instructions */
  authSetup: string | null;
  /** Claude Desktop JSON snippet (already formatted) */
  claudeDesktopConfig: string;
  /** 4-6 concrete use-case cards */
  useCases: McpUseCase[];
  /** 4-6 FAQ items */
  faq: McpFaq[];
  /** Slugs of related MCPs to cross-link */
  related: string[];
  /** Primary keyword phrase for SEO anchor text */
  primaryKeyword: string;
};

// ─── Notion ───────────────────────────────────────────────────────────────────

const notion: McpSeoEntry = {
  slug: "notion-mcp",
  pageTitle: "Notion MCP Server — Use Notion with Claude & AI Agents",
  metaDescription:
    "Connect Notion to Claude Desktop or any AI agent in minutes. The Notion MCP server lets AI read, write, and query your Notion workspace via the official API.",
  headline: "Notion MCP Server",
  subheadline: "Let Claude and AI agents read, write, and search your Notion workspace.",
  category: "Productivity",
  about:
    "Notion is the all-in-one workspace used by millions of teams for docs, wikis, project tracking, and databases. The official Notion API exposes every page, block, database, and comment in your workspace over REST.",
  whyMcp:
    "Wrapping Notion as an MCP lets Claude navigate your workspace like a teammate — querying databases, creating pages, appending notes, and surfacing the right information without copy-paste. It's the fastest way to make AI genuinely useful inside your Notion setup.",
  authTypeName: "API Key",
  authSetup:
    "Go to notion.so/my-integrations → New integration → copy the Internal Integration Token (starts with 'secret_'). Share each page or database you want the MCP to access with your integration.",
  claudeDesktopConfig: `{
  "mcpServers": {
    "notion": {
      "url": "https://api-to-mcp.onrender.com/mcp/notion-mcp",
      "headers": {
        "Authorization": "Bearer secret_YOUR_NOTION_TOKEN"
      }
    }
  }
}`,
  useCases: [
    { icon: "🗂️", title: "Query databases", description: "Ask Claude to filter, sort, and summarise any Notion database — sprints, CRMs, content calendars." },
    { icon: "✍️", title: "Create pages", description: "Tell Claude to draft and publish a new page in any workspace section, pre-filled with your content." },
    { icon: "🔍", title: "Search the workspace", description: "Claude can full-text search across all pages and databases to surface relevant docs instantly." },
    { icon: "💬", title: "Read comments", description: "Pull inline and page-level comments so Claude can summarise feedback threads." },
    { icon: "📋", title: "Append blocks", description: "Claude can append bullet points, to-dos, or code blocks to any existing page without replacing content." },
    { icon: "👥", title: "List users", description: "Fetch workspace members so Claude can tag assignees or build team-aware automations." },
  ],
  faq: [
    { q: "Do I need a Notion Pro plan?", a: "No. The Notion API is available on all plans including Free. You just need to create an integration at notion.so/my-integrations." },
    { q: "Can Claude edit pages I haven't shared?", a: "No. The integration only accesses pages and databases you explicitly share with it. Nothing else is visible." },
    { q: "Is this the official Notion MCP?", a: "This MCP is built from the official Notion REST API spec. It exposes all the same operations Notion documents publicly." },
    { q: "What is the rate limit?", a: "Notion's API allows 3 requests per second per integration. For most AI workloads this is more than enough." },
    { q: "How do I connect this to Claude Desktop?", a: "Paste the JSON config snippet above into your Claude Desktop settings under MCP Servers, replacing YOUR_NOTION_TOKEN with your integration token." },
  ],
  related: ["linear-mcp", "coinbase-mcp"],
  primaryKeyword: "Notion MCP server",
};

// ─── Linear ───────────────────────────────────────────────────────────────────

const linear: McpSeoEntry = {
  slug: "linear-mcp",
  pageTitle: "Linear MCP Server — AI-Powered Issue Tracking with Claude",
  metaDescription:
    "Use Claude to create, update, and search Linear issues. The Linear MCP server connects your issue tracker to any AI agent in one step.",
  headline: "Linear MCP Server",
  subheadline: "Give Claude full access to your Linear workspace — issues, teams, projects, and comments.",
  category: "Productivity",
  about:
    "Linear is the issue tracker of choice for high-velocity engineering teams. Its GraphQL API exposes issues, projects, cycles, teams, and comments — everything your team touches every sprint.",
  whyMcp:
    "With Linear as an MCP, Claude becomes your sprint co-pilot: triage incoming bugs, draft issue descriptions, search for duplicates, post status comments, and update priorities — all in plain English. No more context-switching between your editor and Linear.",
  authTypeName: "API Key",
  authSetup:
    "Open Linear → Settings → API → Personal API keys → Create key. Copy the key (it starts with 'lin_api_'). Pass it as the Authorization header value: 'Bearer lin_api_YOUR_KEY'.",
  claudeDesktopConfig: `{
  "mcpServers": {
    "linear": {
      "url": "https://api-to-mcp.onrender.com/mcp/linear-mcp",
      "headers": {
        "Authorization": "Bearer lin_api_YOUR_KEY"
      }
    }
  }
}`,
  useCases: [
    { icon: "🐛", title: "Triage issues", description: "Ask Claude to list open bugs by priority or assignee and suggest which ones to tackle first." },
    { icon: "✏️", title: "Draft issue descriptions", description: "Describe a problem in plain English and Claude creates a well-structured Linear issue for you." },
    { icon: "🔎", title: "Search for duplicates", description: "Before filing a new issue, Claude searches existing ones so you never create duplicates." },
    { icon: "💬", title: "Post comments", description: "Claude can add status updates or review notes directly to any issue." },
    { icon: "📊", title: "Sprint overview", description: "Get a plain-English summary of the current cycle — what's done, what's blocked, what's at risk." },
    { icon: "🏷️", title: "Update metadata", description: "Bulk-update priority, status, or assignee across multiple issues with a single instruction." },
  ],
  faq: [
    { q: "Which Linear plan is required?", a: "Personal API keys are available on all Linear plans including Free." },
    { q: "Can Claude access all teams?", a: "Yes, a Linear API key grants access to all teams and projects in your workspace by default." },
    { q: "Does this support Linear's GraphQL API?", a: "This MCP is built from Linear's OpenAPI spec and wraps the REST-compatible endpoints. It covers all major CRUD operations." },
    { q: "How do I use this with Cursor?", a: "Add the MCP server URL to Cursor's MCP settings (Settings → MCP) with the Authorization header. Cursor supports the same MCP protocol." },
    { q: "Is my Linear data sent to a third party?", a: "Requests go directly from Claude Desktop to the MCP server, which proxies them to Linear's API. We don't store your issue data." },
  ],
  related: ["notion-mcp", "random-user-mcp"],
  primaryKeyword: "Linear MCP server",
};

// ─── PokéAPI ──────────────────────────────────────────────────────────────────

const pokeapi: McpSeoEntry = {
  slug: "pokeapi-mcp",
  pageTitle: "PokéAPI MCP — Ask Claude Anything About Pokémon",
  metaDescription:
    "The PokéAPI MCP server gives Claude and AI agents access to every Pokémon, type, ability, and generation. No auth required.",
  headline: "PokéAPI MCP Server",
  subheadline: "All the Pokémon data you'll ever need, exposed as an MCP for Claude and AI agents.",
  category: "Games",
  about:
    "PokéAPI is the most comprehensive free Pokémon data source on the internet, covering every game generation since Red and Blue. It returns full data on every Pokémon, move, type, ability, and item — all over a public REST API with no authentication required.",
  whyMcp:
    "As an MCP, PokéAPI lets Claude answer game-related questions, build team compositions, compare stats, and explain type matchups in natural conversation. It's the simplest way to add structured Pokémon knowledge to any AI workflow — especially useful for fan projects, education apps, and gaming tools.",
  authTypeName: "None (public)",
  authSetup: null,
  claudeDesktopConfig: `{
  "mcpServers": {
    "pokeapi": {
      "url": "https://api-to-mcp.onrender.com/mcp/pokeapi-mcp"
    }
  }
}`,
  useCases: [
    { icon: "⚡", title: "Type matchup analysis", description: "Ask Claude which moves are super-effective against a given Pokémon team and get a strategic breakdown." },
    { icon: "🏆", title: "Team building", description: "Claude can suggest a balanced team based on type coverage, base stats, and your preferred playstyle." },
    { icon: "📖", title: "Pokédex queries", description: "Get full stats, evolution chains, and learn sets for any Pokémon by name or number." },
    { icon: "🎮", title: "Generation comparisons", description: "Compare how Pokémon changed across game generations — from Red/Blue all the way to Scarlet/Violet." },
    { icon: "🔬", title: "Ability deep-dives", description: "Explain every ability in plain English, including hidden abilities and their battle effects." },
    { icon: "📚", title: "Fan projects and education", description: "Build quizzes, trivia bots, or educational apps that pull live data without maintaining your own database." },
  ],
  faq: [
    { q: "Do I need an API key?", a: "No. PokéAPI is completely public and free. The MCP server works with no authentication required." },
    { q: "How many Pokémon does it cover?", a: "PokéAPI covers all Pokémon through the latest main series games, including regional forms and variants." },
    { q: "What's the rate limit?", a: "PokéAPI is fair-use with a generous rate limit. The MCP server respects these limits automatically." },
    { q: "Can I use this in a Node.js app?", a: "Yes. Any MCP-compatible client can connect to this server — Claude Desktop, Cursor, Cline, or a custom integration." },
  ],
  related: ["nasa-apod-mcp", "jokeapi-mcp"],
  primaryKeyword: "PokéAPI MCP server",
};

// ─── NASA APOD ────────────────────────────────────────────────────────────────

const nasaApod: McpSeoEntry = {
  slug: "nasa-apod-mcp",
  pageTitle: "NASA APOD MCP — Astronomy Pictures via Claude and AI Agents",
  metaDescription:
    "Connect NASA's Astronomy Picture of the Day API to Claude. The NASA APOD MCP server returns daily space images, explanations, and date ranges — no auth needed.",
  headline: "NASA APOD MCP Server",
  subheadline: "Bring NASA's daily astronomy picture into Claude and any AI agent — completely free.",
  category: "Science",
  about:
    "NASA's Astronomy Picture of the Day (APOD) API has delivered a new space image every single day since 1995, with expert explanations written by astronomers. The API is public, free, and backed by NASA's own infrastructure.",
  whyMcp:
    "As an MCP, APOD lets Claude fetch and describe today's space image, pull historical images for any date, or return a random set for exploration. It's perfect for science education apps, daily briefing workflows, and anyone who wants to bring the universe into their AI conversations.",
  authTypeName: "None (public)",
  authSetup: null,
  claudeDesktopConfig: `{
  "mcpServers": {
    "nasa-apod": {
      "url": "https://api-to-mcp.onrender.com/mcp/nasa-apod-mcp"
    }
  }
}`,
  useCases: [
    { icon: "🌌", title: "Daily space briefing", description: "Start each morning with Claude describing today's APOD image and its scientific context." },
    { icon: "📅", title: "Historical lookups", description: "Fetch the astronomy image for any specific date — birthdays, anniversaries, historical events." },
    { icon: "🎲", title: "Random exploration", description: "Pull a random set of APOD images for trivia, inspiration, or educational content generation." },
    { icon: "🏫", title: "Science education", description: "Build classroom tools where students ask Claude to explain space phenomena using NASA's official images." },
    { icon: "📰", title: "Automated space digests", description: "Generate weekly summaries of APOD images and descriptions for newsletters or social posts." },
    { icon: "🔭", title: "Astronomy Q&A", description: "Pair the image data with Claude's knowledge to answer deep questions about the objects pictured." },
  ],
  faq: [
    { q: "Do I need a NASA API key?", a: "The MCP works with NASA's DEMO_KEY for light usage. For heavier use, get a free key at api.nasa.gov — no cost, instant approval." },
    { q: "How far back does the archive go?", a: "APOD archives go back to June 16, 1995, giving you over 10,000 images to explore." },
    { q: "Does it return video as well as images?", a: "Yes. Some APOD entries are YouTube videos. The API returns the media type so Claude can handle them differently." },
    { q: "Is this an official NASA product?", a: "This MCP is built from NASA's public APOD API. It is not officially affiliated with NASA, but uses only their public, documented endpoints." },
  ],
  related: ["pokeapi-mcp", "coinbase-mcp"],
  primaryKeyword: "NASA APOD MCP server",
};

// ─── Coinbase ─────────────────────────────────────────────────────────────────

const coinbase: McpSeoEntry = {
  slug: "coinbase-mcp",
  pageTitle: "Coinbase MCP Server — Live Crypto Prices for Claude & AI Agents",
  metaDescription:
    "Get real-time Bitcoin, Ethereum, and crypto prices via Claude. The Coinbase public MCP server returns spot, buy, and sell prices with no auth required.",
  headline: "Coinbase Public API MCP Server",
  subheadline: "Real-time cryptocurrency prices and exchange rates — directly in Claude, no account needed.",
  category: "Finance",
  about:
    "Coinbase's public data API provides real-time spot, buy, and sell prices for hundreds of cryptocurrencies against any fiat currency. It requires no API key and no account — just a public HTTPS endpoint anyone can call.",
  whyMcp:
    "As an MCP, the Coinbase public API lets Claude answer live price questions, calculate conversions, compare assets, and build crypto-aware automations — all in natural language. Since no auth is required, it's the fastest way to add financial data to any AI workflow.",
  authTypeName: "None (public)",
  authSetup: null,
  claudeDesktopConfig: `{
  "mcpServers": {
    "coinbase": {
      "url": "https://api-to-mcp.onrender.com/mcp/coinbase-mcp"
    }
  }
}`,
  useCases: [
    { icon: "💰", title: "Live price lookups", description: "Ask Claude for the current BTC, ETH, or SOL price in any fiat currency, updated in real time." },
    { icon: "💱", title: "Currency conversions", description: "Convert between crypto and fiat — 'how much is 0.5 ETH in EUR?' — with live rates." },
    { icon: "📊", title: "Market comparisons", description: "Compare buy vs sell spreads across multiple assets to understand market depth at a glance." },
    { icon: "🤖", title: "Automated price alerts", description: "Build simple agents that check prices on a schedule and surface them in Slack, email, or a dashboard." },
    { icon: "📈", title: "Portfolio calculations", description: "Combine live prices with your holdings data to get real-time portfolio values from Claude." },
    { icon: "📚", title: "Finance education", description: "Use live data to teach crypto concepts — spreads, exchange rates, and market mechanics — with real examples." },
  ],
  faq: [
    { q: "Does this require a Coinbase account?", a: "No. The public Coinbase API is completely open. No account, no key, no signup required." },
    { q: "How real-time are the prices?", a: "Prices are pulled live from Coinbase's API on every request. There's no caching of price data." },
    { q: "Which cryptocurrencies are supported?", a: "All currencies listed on Coinbase are supported — hundreds of assets against any major fiat currency." },
    { q: "Can I use this for trading decisions?", a: "This API provides public market data only. It does not access your Coinbase account or execute trades. Always do your own research before trading." },
  ],
  related: ["nasa-apod-mcp", "notion-mcp"],
  primaryKeyword: "Coinbase crypto MCP server",
};

// ─── Random User ──────────────────────────────────────────────────────────────

const randomUser: McpSeoEntry = {
  slug: "random-user-mcp",
  pageTitle: "Random User Generator MCP — Test Data for Claude & AI Agents",
  metaDescription:
    "Generate realistic fake user profiles for testing via Claude. The Random User Generator MCP returns names, emails, avatars, and addresses on demand.",
  headline: "Random User Generator MCP Server",
  subheadline: "Generate realistic test user data on demand — names, emails, avatars, addresses — via any AI agent.",
  category: "Development",
  about:
    "The Random User Generator API produces realistic synthetic user profiles — full names, email addresses, profile photos, physical addresses, phone numbers, and more — across dozens of nationalities. It's the go-to API for developers who need realistic test data without inventing it manually.",
  whyMcp:
    "As an MCP, the Random User Generator lets Claude produce seeded test data sets, populate demo UIs, generate mock personas for user research, and build realistic fixtures without you leaving your coding environment.",
  authTypeName: "None (public)",
  authSetup: null,
  claudeDesktopConfig: `{
  "mcpServers": {
    "random-user": {
      "url": "https://api-to-mcp.onrender.com/mcp/random-user-mcp"
    }
  }
}`,
  useCases: [
    { icon: "🧪", title: "Test data generation", description: "Ask Claude to generate 50 realistic users in JSON format, filtered by nationality, for seeding a test database." },
    { icon: "🎨", title: "UI prototyping", description: "Populate your prototype or Figma with realistic names, avatars, and addresses that look real." },
    { icon: "📋", title: "Mock personas", description: "Create user research personas with realistic demographic data to pressure-test your product assumptions." },
    { icon: "🔧", title: "API testing fixtures", description: "Generate fixture files in one command instead of hand-writing fake data in your test suites." },
    { icon: "🌍", title: "Localisation testing", description: "Request users filtered by nationality (US, GB, FR, IN...) to test your localisation and character encoding." },
    { icon: "📊", title: "Demo datasets", description: "Build convincing product demos with realistic user tables that don't expose real customer data." },
  ],
  faq: [
    { q: "Is the generated data truly random?", a: "Yes. Each API call returns a freshly generated set. You can also pass a seed parameter for reproducible results." },
    { q: "What fields are included?", a: "Name, email, phone, address, date of birth, nationality, username, password hash, and a profile photo URL." },
    { q: "Is this GDPR-safe?", a: "Yes. All data is completely synthetic — there are no real people behind these profiles." },
    { q: "Can I filter by gender or nationality?", a: "Yes. Pass gender=male/female and nat=US/GB/FR/etc. to get targeted results." },
  ],
  related: ["jokeapi-mcp", "pokeapi-mcp"],
  primaryKeyword: "random user generator MCP",
};

// ─── JokeAPI ──────────────────────────────────────────────────────────────────

const jokeApi: McpSeoEntry = {
  slug: "jokeapi-mcp",
  pageTitle: "JokeAPI MCP — Add Humor to Claude and AI Agent Workflows",
  metaDescription:
    "Fetch programmer jokes, puns, and one-liners via Claude with the JokeAPI MCP server. Filterable by category, type, and safe mode. No auth required.",
  headline: "JokeAPI MCP Server",
  subheadline: "Fetch categorised jokes on demand — programming, puns, dark humour — directly in Claude.",
  category: "Entertainment",
  about:
    "JokeAPI is a free, public joke API with hundreds of jokes across categories including Programming, Misc, Dark, Pun, Spooky, and Christmas. It supports filtering by joke type (single vs two-part), content flags, and a safe mode for family-friendly environments.",
  whyMcp:
    "As an MCP, JokeAPI lets Claude add a comedic layer to any workflow — daily standup openers, Slack bot responses, onboarding copy, or just a mood-lifter mid-conversation. It's lightweight, public, and needs zero configuration.",
  authTypeName: "None (public)",
  authSetup: null,
  claudeDesktopConfig: `{
  "mcpServers": {
    "jokeapi": {
      "url": "https://api-to-mcp.onrender.com/mcp/jokeapi-mcp"
    }
  }
}`,
  useCases: [
    { icon: "☕", title: "Standup openers", description: "Start every daily standup with a programming joke fetched live, filtered to be workplace-safe." },
    { icon: "🤖", title: "Slack bots", description: "Build a /joke Slack command that pulls a fresh joke from JokeAPI every time someone needs a laugh." },
    { icon: "🎓", title: "Coding tutorials", description: "Pepper programming tutorials with relevant jokes to keep learners engaged and the tone light." },
    { icon: "🎄", title: "Holiday content", description: "Generate Christmas or seasonal jokes for newsletters, social posts, and campaign copy." },
    { icon: "✍️", title: "Creative writing", description: "Use jokes as creative prompts — Claude can riff on a pun or build a story around a setup." },
    { icon: "🧩", title: "Trivia games", description: "Build quiz apps with joke rounds — two-part setup/punchline jokes work perfectly as timed reveals." },
  ],
  faq: [
    { q: "How do I filter out offensive jokes?", a: "Enable safe mode in the request and JokeAPI filters out all NSFW, racist, sexist, and explicit content." },
    { q: "What categories are available?", a: "Programming, Misc, Dark, Pun, Spooky, and Christmas. You can request one or multiple categories per call." },
    { q: "What's the difference between single and two-part jokes?", a: "Single jokes are delivered in one line. Two-part jokes have a setup and a punchline, returned as separate fields." },
    { q: "Is there a rate limit?", a: "JokeAPI is free and rate-limited fairly. For most conversational use cases you'll never hit the ceiling." },
  ],
  related: ["random-user-mcp", "pokeapi-mcp"],
  primaryKeyword: "JokeAPI MCP server",
};

// ─── Stripe ───────────────────────────────────────────────────────────────────

const stripe: McpSeoEntry = {
  slug: "stripe-mcp",
  pageTitle: "Stripe MCP Server — Manage Payments & Billing with Claude",
  metaDescription:
    "Connect Stripe to Claude Desktop or any AI agent. The Stripe MCP server lets Claude list customers, manage subscriptions, query charges, and retrieve balance data via the Stripe API.",
  headline: "Stripe MCP Server",
  subheadline: "Let Claude query customers, subscriptions, charges, and payment intents directly from your Stripe account.",
  category: "Finance",
  about:
    "Stripe is the world's leading payment infrastructure platform, used by millions of businesses to accept payments, manage subscriptions, and handle billing. Its REST API exposes every object in your account — customers, invoices, charges, subscriptions, products, and more.",
  whyMcp:
    "With Stripe as an MCP, Claude becomes your billing co-pilot: look up a customer's payment history, check a subscription status, retrieve failed charges, summarise revenue trends — all in plain English without opening the Stripe dashboard. It's the fastest way to answer billing questions during a support call or team standup.",
  authTypeName: "API Key",
  authSetup:
    "Go to dashboard.stripe.com → Developers → API keys. Copy your Secret key (starts with 'sk_test_' for test mode or 'sk_live_' for production). Pass it as the Authorization header: 'Bearer sk_test_YOUR_KEY'. Use test mode keys while experimenting.",
  claudeDesktopConfig: `{
  "mcpServers": {
    "stripe": {
      "url": "https://api-to-mcp.onrender.com/mcp/stripe-mcp",
      "headers": {
        "Authorization": "Bearer sk_test_YOUR_STRIPE_KEY"
      }
    }
  }
}`,
  useCases: [
    { icon: "👤", title: "Look up customers", description: "Ask Claude to find a customer by email, retrieve their full profile, and list their payment methods." },
    { icon: "💳", title: "Query charges", description: "Get recent charges, check status (succeeded/failed/refunded), and pull dispute details in plain English." },
    { icon: "🔄", title: "Subscription status", description: "Instantly check if a customer's subscription is active, past due, or cancelled without opening the dashboard." },
    { icon: "📊", title: "Revenue snapshots", description: "Ask Claude to retrieve your current balance, pending payouts, and recent transaction volumes." },
    { icon: "🧾", title: "Invoice lookup", description: "Find unpaid invoices, check due dates, and get totals for any customer or date range." },
    { icon: "📦", title: "Product & price catalog", description: "List all your Stripe products and prices — useful when building new checkout flows or auditing your catalog." },
  ],
  faq: [
    { q: "Is it safe to use my Stripe secret key?", a: "Use a restricted key with read-only permissions for non-sensitive queries. Go to Stripe Dashboard → Developers → Restricted keys to create a key with only the permissions Claude needs." },
    { q: "Can Claude execute payments or refunds?", a: "This MCP exposes Stripe's full API. If your key has write permissions, Claude can create refunds and payment intents. Use a restricted read-only key to limit access." },
    { q: "Does this work with Stripe test mode?", a: "Yes. Use your test secret key (sk_test_...) to query test data without touching live transactions." },
    { q: "Which Stripe API version does this use?", a: "The MCP is built from Stripe's official OpenAPI spec and targets their current stable API version." },
    { q: "How do I connect this to Cursor?", a: "Add the MCP server URL to Cursor's MCP settings (Settings → MCP) with the Authorization header. Cursor supports the same MCP protocol as Claude Desktop." },
  ],
  related: ["notion-mcp", "coinbase-mcp", "github-mcp"],
  primaryKeyword: "Stripe MCP server",
};

// ─── GitHub ───────────────────────────────────────────────────────────────────

const github: McpSeoEntry = {
  slug: "github-mcp",
  pageTitle: "GitHub MCP Server — Manage Repos, Issues & PRs with Claude",
  metaDescription:
    "Connect GitHub to Claude Desktop or Cursor. The GitHub MCP server lets Claude list repos, create issues, review pull requests, read files, and search code via the GitHub REST API.",
  headline: "GitHub MCP Server",
  subheadline: "Give Claude access to your GitHub repos, issues, pull requests, and code — all in natural language.",
  category: "Development",
  about:
    "GitHub is the world's largest code hosting platform, home to over 100 million repositories. Its REST API exposes repos, issues, pull requests, commits, branches, file contents, and code search — everything developers interact with every day.",
  whyMcp:
    "With GitHub as an MCP, Claude becomes an active contributor to your workflow: triage issues, summarise open PRs, read file contents, check CI status, and draft release notes — without leaving your conversation. It's the fastest way to get an AI pair-programmer that actually understands your repository.",
  authTypeName: "API Key",
  authSetup:
    "Go to github.com → Settings → Developer settings → Personal access tokens → Tokens (classic). Generate a new token and select the 'repo' scope (add 'read:user' if you want profile access). Copy the token (starts with 'ghp_') and pass it as the Authorization header.",
  claudeDesktopConfig: `{
  "mcpServers": {
    "github": {
      "url": "https://api-to-mcp.onrender.com/mcp/github-mcp",
      "headers": {
        "Authorization": "Bearer ghp_YOUR_GITHUB_TOKEN"
      }
    }
  }
}`,
  useCases: [
    { icon: "🐛", title: "Triage issues", description: "Ask Claude to list open issues by label, assignee, or milestone and suggest which ones to tackle first." },
    { icon: "🔀", title: "Summarise pull requests", description: "Get a plain-English summary of any open PR — what changed, why, and what reviewers should focus on." },
    { icon: "📁", title: "Read file contents", description: "Claude can fetch any file from any branch and explain the code, review it, or suggest improvements." },
    { icon: "🔍", title: "Search code", description: "Search across all your repositories for a function, pattern, or string — Claude surfaces the relevant files." },
    { icon: "📝", title: "Draft release notes", description: "List commits between two tags and ask Claude to write release notes in your preferred format." },
    { icon: "🌿", title: "Branch overview", description: "List all branches, compare divergence from main, and identify stale branches ready to clean up." },
  ],
  faq: [
    { q: "Which GitHub plan is required?", a: "Personal access tokens work on Free, Pro, Team, and Enterprise plans. The 'repo' scope covers both public and private repositories." },
    { q: "Can Claude create issues or push code?", a: "Yes, if your token has write permissions. Use a fine-grained personal access token with only read permissions if you prefer Claude to stay read-only." },
    { q: "Does this work with GitHub Enterprise?", a: "The MCP targets the public GitHub API (api.github.com). GitHub Enterprise Server has a compatible REST API but requires a different base URL." },
    { q: "How do I use this in Cursor?", a: "Add the MCP URL to Cursor's MCP settings with the Authorization header. Cursor's MCP support is identical to Claude Desktop." },
    { q: "Is this the official GitHub MCP?", a: "This MCP is built from GitHub's official OpenAPI spec and exposes all publicly documented REST API endpoints. It is not an official GitHub product." },
  ],
  related: ["linear-mcp", "notion-mcp", "slack-mcp"],
  primaryKeyword: "GitHub MCP server",
};

// ─── Slack ────────────────────────────────────────────────────────────────────

const slack: McpSeoEntry = {
  slug: "slack-mcp",
  pageTitle: "Slack MCP Server — Send Messages & Search Slack with Claude",
  metaDescription:
    "Connect Slack to Claude Desktop or any AI agent. The Slack MCP server lets Claude read channels, send messages, search conversations, and list workspace users via the Slack API.",
  headline: "Slack MCP Server",
  subheadline: "Let Claude read your Slack channels, send messages, search conversations, and surface the right information from your workspace.",
  category: "Productivity",
  about:
    "Slack is the team communication hub for millions of organisations worldwide. Its Web API exposes channels, messages, users, files, and search across your entire workspace — everything your team communicates through, accessible over REST.",
  whyMcp:
    "With Slack as an MCP, Claude can search your workspace for decisions made in past threads, post summaries to channels, look up team members, and surface relevant conversations — all without leaving your AI workflow. It turns your message history into a queryable knowledge base.",
  authTypeName: "API Key",
  authSetup:
    "Go to api.slack.com/apps → Create New App → From scratch. Under OAuth & Permissions, add Bot Token Scopes: channels:read, chat:write, search:read, users:read. Click 'Install to Workspace' and copy the Bot User OAuth Token (starts with 'xoxb-'). Pass it as the Authorization header.",
  claudeDesktopConfig: `{
  "mcpServers": {
    "slack": {
      "url": "https://api-to-mcp.onrender.com/mcp/slack-mcp",
      "headers": {
        "Authorization": "Bearer xoxb-YOUR_SLACK_BOT_TOKEN"
      }
    }
  }
}`,
  useCases: [
    { icon: "🔍", title: "Search conversations", description: "Ask Claude to find past decisions, meeting notes, or announcements buried in Slack threads." },
    { icon: "📢", title: "Post messages", description: "Tell Claude to post a summary, status update, or announcement to any channel on your behalf." },
    { icon: "👥", title: "Look up team members", description: "Find a colleague's display name, timezone, or status without leaving your current workflow." },
    { icon: "📋", title: "Channel overview", description: "List all public channels, see member counts, and get topic summaries to navigate large workspaces." },
    { icon: "📖", title: "Read thread history", description: "Pull the last N messages from any channel so Claude can summarise what was discussed." },
    { icon: "🤖", title: "Slack bot automation", description: "Build AI workflows that monitor channels and respond to keywords or questions automatically." },
  ],
  faq: [
    { q: "What Slack plan is required?", a: "The Slack API is available on all plans including Free. Bot Token Scopes determine what the MCP can access." },
    { q: "Can Claude read private channels?", a: "Only channels the bot has been invited to. Private channels stay private unless you explicitly add the bot." },
    { q: "How do I add the bot to a channel?", a: "In any channel, type /invite @your-bot-name. The bot can then read history and post messages in that channel." },
    { q: "Can Claude send DMs?", a: "Yes, if you add the 'im:write' scope to your bot token. Claude can open a DM and send a message to any workspace member." },
    { q: "Is message data stored anywhere?", a: "Requests go directly from Claude Desktop to the MCP server, which proxies them to Slack's API. Message content is not stored by the MCP platform." },
  ],
  related: ["notion-mcp", "linear-mcp", "github-mcp"],
  primaryKeyword: "Slack MCP server",
};

// ─── Export ───────────────────────────────────────────────────────────────────

export const MCP_SEO_DATA: McpSeoEntry[] = [
  notion,
  linear,
  pokeapi,
  nasaApod,
  coinbase,
  randomUser,
  jokeApi,
  stripe,
  github,
  slack,
];

export function getMcpSeoBySlug(slug: string): McpSeoEntry | undefined {
  return MCP_SEO_DATA.find((e) => e.slug === slug);
}
